import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { isOrganizationAdmin } from '../utils/planPermissions';

const USAGE_BLOCKED_STORAGE_KEY = 'usage_blocked_notification_shown';
const USAGE_BLOCKED_CACHE_KEY = 'usage_blocked_cache';

const useUsageNotifications = (user) => {
  const [notificationData, setNotificationData] = useState(null);
  const [showNotification, setShowNotification] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isUsageBlocked, setIsUsageBlocked] = useState(false);
  const [cachedBlockedState, setCachedBlockedState] = useState(null);

  // Check if user is admin or owner
  const isAdminOrOwner = isOrganizationAdmin();

  // Initialize cached blocked state from localStorage
  useEffect(() => {
    const getCachedBlockedState = () => {
      try {
        const cached = localStorage.getItem(USAGE_BLOCKED_CACHE_KEY);
        if (cached) {
          const parsedCache = JSON.parse(cached);
          // Check if cache is still valid (same billing period)
          const now = new Date();
          const cacheTime = new Date(parsedCache.timestamp);
          const hoursSinceCache = (now - cacheTime) / (1000 * 60 * 60);
          
          // Cache is valid for 24 hours or until billing period changes
          if (hoursSinceCache < 24 && parsedCache.creditRefreshDate) {
            const refreshDate = new Date(parsedCache.creditRefreshDate);
            // If we're still in the same billing period, use cached state
            if (now < refreshDate) {
              return parsedCache;
            }
          }
        }
      } catch (error) {
        console.error('Error reading cached blocked state:', error);
        localStorage.removeItem(USAGE_BLOCKED_CACHE_KEY);
      }
      return null;
    };

    const cached = getCachedBlockedState();
    if (cached) {
      setCachedBlockedState(cached);
      setIsUsageBlocked(cached.isUsageBlocked);
      if (cached.isUsageBlocked) {
        setNotificationData(cached.notificationData);
        if (!isAdminOrOwner) {
          setShowNotification(true);
        }
      }
    }
  }, [isAdminOrOwner]);

  // Cache blocked state to localStorage
  const updateBlockedCache = useCallback((blocked, data) => {
    try {
      const cacheData = {
        isUsageBlocked: blocked,
        notificationData: data,
        creditRefreshDate: data?.creditRefreshDate,
        timestamp: new Date().toISOString()
      };
      localStorage.setItem(USAGE_BLOCKED_CACHE_KEY, JSON.stringify(cacheData));
      setCachedBlockedState(cacheData);
    } catch (error) {
      console.error('Error caching blocked state:', error);
    }
  }, []);

  // Clear blocked cache
  const clearBlockedCache = useCallback(() => {
    localStorage.removeItem(USAGE_BLOCKED_CACHE_KEY);
    setCachedBlockedState(null);
  }, []);

  // Fetch usage blocking status for any user
  const fetchUsageBlockingStatus = useCallback(async (forceRefresh = false) => {
    if (!user) return;

    // If we have a cached blocked state and it's still valid, use it (unless force refresh)
    if (cachedBlockedState && !forceRefresh) {
      const now = new Date();
      const refreshDate = new Date(cachedBlockedState.creditRefreshDate);
      const cacheTime = new Date(cachedBlockedState.timestamp);
      const hoursSinceCache = (now - cacheTime) / (1000 * 60 * 60);
      
      // Use cache if it's less than 1 hour old and we're still in the same billing period
      if (hoursSinceCache < 1 && now < refreshDate) {
        return;
      }
    }

    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/team-billing/usage-blocking-status`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        const data = response.data.data;
        
        // Handle usage blocking scenario
        if (data.isUsageBlocked) {
          setIsUsageBlocked(true);
          updateBlockedCache(true, data);
          
          // For non-admin users, set notification data and show notification
          if (!isAdminOrOwner) {
            setNotificationData(data);
            setShowNotification(true);
          }
          
          // Check if we've already shown this notification for this billing period
          const storedData = localStorage.getItem(USAGE_BLOCKED_STORAGE_KEY);
          if (storedData) {
            try {
              const parsed = JSON.parse(storedData);
              const storedRefreshDate = new Date(parsed.creditRefreshDate);
              const currentRefreshDate = new Date(data.creditRefreshDate);
              
              // If it's a new billing period, reset the notification
              if (storedRefreshDate.getTime() !== currentRefreshDate.getTime()) {
                localStorage.removeItem(USAGE_BLOCKED_STORAGE_KEY);
                // Show notification again for new billing period
                if (!isAdminOrOwner) {
                  setShowNotification(true);
                }
              } else {
                // If we've acknowledged this billing period, don't show notification
                if (!isAdminOrOwner) {
                  setShowNotification(false);
                }
              }
            } catch (e) {
              // If parsing fails, clear the storage
              localStorage.removeItem(USAGE_BLOCKED_STORAGE_KEY);
              // Show notification
              if (!isAdminOrOwner) {
                setShowNotification(true);
              }
            }
          }
        } else {
          setIsUsageBlocked(false);
          clearBlockedCache();
          // Clear any stored blocked notification state when usage is no longer blocked
          localStorage.removeItem(USAGE_BLOCKED_STORAGE_KEY);
          // Hide notification for non-admin users when usage is no longer blocked
          if (!isAdminOrOwner) {
            setShowNotification(false);
            setNotificationData(null);
          }
        }
      }
    } catch (err) {
      console.error('Error fetching usage blocking status:', err);
      // Don't set error state for blocking status as it's less critical
    }
  }, [user, isAdminOrOwner, cachedBlockedState, updateBlockedCache, clearBlockedCache]);

  // Fetch usage notification status (admin/owner only)
  const fetchNotificationStatus = useCallback(async () => {
    // Only fetch for admin/owner users
    if (!user || !isAdminOrOwner) {
      return;
    }

    try {
      setIsLoading(true);
      setError(null);

      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/team-billing/usage-notification-status`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        const data = response.data.data;
        setNotificationData(data);
        
        // For admins/owners, show notification if needed
        if (data.shouldShowNotification || data.isUsageBlocked) {
          setShowNotification(true);
        } else {
          setShowNotification(false);
        }
      }
    } catch (err) {
      console.error('Error fetching usage notification status:', err);
      setError(err.response?.data?.message || 'Failed to fetch usage notification status');
    } finally {
      setIsLoading(false);
    }
  }, [user, isAdminOrOwner]);

  // Dismiss notification (only works for non-blocking notifications)
  const dismissNotification = useCallback(async () => {
    if (!notificationData) return;

    // If this is a blocking notification, don't allow dismissal
    if (isUsageBlocked) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${process.env.REACT_APP_API_URL}/team-billing/dismiss-usage-notification`,
        { usagePercentage: notificationData.usagePercentage },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setShowNotification(false);
      setNotificationData(null);
    } catch (err) {
      console.error('Error dismissing notification:', err);
      throw err;
    }
  }, [notificationData, isUsageBlocked]);

  // Acknowledge the blocking notification (just hides UI temporarily)
  const acknowledgeBlockingNotification = useCallback(() => {
    if (!notificationData || !isUsageBlocked) return;

    // Store acknowledgment in local storage
    const acknowledgmentData = {
      creditRefreshDate: notificationData.creditRefreshDate,
      acknowledgedAt: new Date().toISOString()
    };
    localStorage.setItem(USAGE_BLOCKED_STORAGE_KEY, JSON.stringify(acknowledgmentData));
    
    // Hide notification temporarily
    setShowNotification(false);
  }, [notificationData, isUsageBlocked]);

  // Enable usage-based pricing
  const enableUsageBasedPricing = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${process.env.REACT_APP_API_URL}/team-billing/enable-usage-based-pricing`,
        {
          enabled: true,
          capType: 'capped',
          overageCapAmount: 20,
          agreementAccepted: true
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      // Clear blocked cache since usage-based pricing is now enabled
      clearBlockedCache();
      setIsUsageBlocked(false);
      setShowNotification(false);
      setNotificationData(null);
      
      // Force refresh to get updated status
      setTimeout(() => {
        fetchUsageBlockingStatus(true);
        fetchNotificationStatus();
      }, 1000);
    } catch (err) {
      console.error('Error enabling usage-based pricing:', err);
      throw err;
    }
  }, [clearBlockedCache, fetchUsageBlockingStatus, fetchNotificationStatus]);

  // Initial fetch on mount
  useEffect(() => {
    fetchUsageBlockingStatus(); // Check blocking for all users
    fetchNotificationStatus(); // Check notifications for admin/owner only
  }, [fetchUsageBlockingStatus, fetchNotificationStatus]);

  // Poll for updates - different frequencies based on blocked state
  useEffect(() => {
    // Use longer interval when blocked (5 minutes) since we have cached state
    // Use shorter interval when not blocked (30 seconds) for normal monitoring
    const pollInterval = isUsageBlocked ? 5 * 60 * 1000 : 30 * 1000;
    
    const interval = setInterval(() => {
      fetchUsageBlockingStatus(); // Check blocking for all users
      if (isAdminOrOwner) {
        fetchNotificationStatus(); // Check notifications for admin/owner only
      }
    }, pollInterval);
    
    return () => clearInterval(interval);
  }, [fetchUsageBlockingStatus, fetchNotificationStatus, isAdminOrOwner, isUsageBlocked]);

  return {
    notificationData,
    showNotification,
    isLoading,
    error,
    isAdminOrOwner,
    isUsageBlocked,
    dismissNotification,
    acknowledgeBlockingNotification,
    enableUsageBasedPricing,
    refreshNotificationStatus: () => {
      fetchUsageBlockingStatus(true); // Force refresh
      fetchNotificationStatus();
    }
  };
};

export default useUsageNotifications; 